import React from "react";
import { Alert, Box, Chip, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Star, StarBorder } from "@mui/icons-material";
import CommonDataGrid from "../components/grid/CommonDataGrid";
import SectionHeader from "../components/common/SectionHeader";
import { marketDataApi } from "../services/marketDataApi";

const WATCHLIST_KEY = "captin_watchlist_symbols";

const WatchlistPage = () => {
  const { data: marketAssets = [], isLoading, error } = marketDataApi.endpoints.getAllMarketAssets.useQuery();
  const [watchlistSymbols, setWatchlistSymbols] = React.useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(WATCHLIST_KEY) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  React.useEffect(() => {
    localStorage.setItem(WATCHLIST_KEY, JSON.stringify(watchlistSymbols));
  }, [watchlistSymbols]);

  const rows = React.useMemo(
    () => marketAssets.filter((asset) => watchlistSymbols.includes(asset.symbol)),
    [marketAssets, watchlistSymbols]
  );

  const toggleWatchlist = (symbol) => {
    setWatchlistSymbols((prev) =>
      prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]
    );
  };

  const columns = React.useMemo(
    () => [
      {
        field: "symbol",
        headerName: "Symbol",
        valueGetter: (row) => row.symbol,
        renderCell: (row) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={watchlistSymbols.includes(row.symbol) ? "Remove from watchlist" : "Add to watchlist"}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleWatchlist(row.symbol); }}>
                {watchlistSymbols.includes(row.symbol) ? <Star color="warning" fontSize="small" /> : <StarBorder fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.symbol}</Typography>
          </Stack>
        ),
      },
      { field: "name", headerName: "Asset", valueGetter: (row) => row.name },
      {
        field: "currentPrice",
        headerName: "Price",
        align: "right",
        valueGetter: (row) => Number(row.currentPrice || 0),
      },
      {
        field: "priceChangePercentage24h",
        headerName: "24h %",
        align: "right",
        valueGetter: (row) => Number(row.priceChangePercentage24h || 0),
      },
      {
        field: "assetType",
        headerName: "Type",
        renderCell: (row) => (
          <Chip
            size="small"
            color={row.assetType === 0 || row.assetType === "Crypto" ? "secondary" : "primary"}
            label={row.assetType === 0 || row.assetType === "Crypto" ? "Crypto" : "Stock"}
          />
        ),
      },
    ],
    [watchlistSymbols]
  );

  if (isLoading) return <LinearProgress />;
  if (error) return <Alert severity="error">Failed to load watchlist assets.</Alert>;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <SectionHeader variant="h4" component="h1" gutterBottom sx={{ pb: 0.75 }}>
        Watchlist
      </SectionHeader>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Track selected assets and monitor their latest price movement.
      </Typography>
      <CommonDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.marketAssetId}
        initialSortField="symbol"
        initialSortDirection="asc"
        filterPlaceholder="Filter watchlist by symbol or asset..."
      />
    </Box>
  );
};

export default WatchlistPage;
